// Offline (read-only) vault data source — desktop §15. When the server is unreachable, the user
// unlocks the local encrypted snapshot with their master password; this store holds the decrypted
// vaults + folder/device tree + items so the offline UI mirrors the online one. Purely local.
import { create } from "zustand";
import type { Organization } from "../api/client";
import { offlineUnlock } from "../desktop/bridge";
import type { DeviceMeta } from "./devices";
import type { FieldType } from "./catalog";
import { templateFor } from "./catalog";
import type { DecryptedItem, Field } from "./items";

/** Native snapshot item (flat plaintext field map + org/folder refs). */
export interface OfflineItem {
  id: string;
  org_id: string;
  folder_id?: string | null;
  item_type: string;
  name: string;
  fields: Record<string, string>;
}
export interface OfflineOrg {
  id: string;
  name: string;
  is_personal: boolean;
}
export interface OfflineFolder {
  id: string;
  org_id: string;
  parent_id?: string | null;
  name: string;
  kind: "folder" | "device";
  meta?: string | null; // device metadata JSON string
}
export interface OfflineSnapshot {
  version: number;
  orgs: OfflineOrg[];
  folders: OfflineFolder[];
  items: OfflineItem[];
}

/** Flat folder shape AppShell's buildFolderTree expects. */
export interface OfflineFlatFolder {
  id: string;
  parentId: string | null;
  name: string;
  kind: "folder" | "device";
  meta: DeviceMeta | null;
}

/** Adapt a native OfflineItem into the DecryptedItem shape used everywhere in the UI. */
export function adaptOfflineItem(o: OfflineItem): DecryptedItem {
  const tmpl = templateFor(o.item_type);
  const seen = new Set<string>();
  const fields: Field[] = [];
  for (const ft of tmpl.fields) {
    seen.add(ft.key);
    const value = o.fields[ft.key];
    if (value == null || value === "") continue;
    fields.push({ key: ft.key, label: ft.label, type: ft.type, value, secret: ft.secret, multiline: ft.multiline });
  }
  for (const [k, v] of Object.entries(o.fields)) {
    if (seen.has(k) || !v || k === "notes") continue;
    fields.push({ key: k, label: k, type: "text" as FieldType, value: v, secret: /pass|secret|key|token/i.test(k) });
  }
  return {
    id: o.id,
    type: o.item_type,
    revision: 0,
    folderId: o.folder_id ?? null,
    content: { name: o.name, tags: [], notes: o.fields.notes ?? "", fields },
    offlineAllowed: true,
    updatedAt: undefined,
  };
}

/** Flatten an item's fields into a {key:value} map for the params-based launch commands. */
export function launchFieldsOf(it: DecryptedItem): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of it.content.fields) if (f.value) out[f.key] = f.value;
  return out;
}

interface OfflineState {
  active: boolean; // an offline snapshot is currently unlocked + rendered
  orgs: Organization[]; // vaults (synthetic wrapped_org_key — never used offline)
  snapItems: OfflineItem[];
  snapFolders: OfflineFolder[];
  error: string | null;
  /** True when the cache is gone (wiped after too many attempts / expired / missing) — no retry. */
  terminal: boolean;
  enter(password: string): Promise<boolean>;
  exit(): void;
}

/** Read-only offline snapshot store. */
export const useOffline = create<OfflineState>((set) => ({
  active: false,
  orgs: [],
  snapItems: [],
  snapFolders: [],
  error: null,
  terminal: false,
  enter: async (password) => {
    set({ error: null });
    try {
      const snap = (await offlineUnlock(password)) as unknown as OfflineSnapshot;
      const orgs: Organization[] = snap.orgs.map((o) => ({ id: o.id, name: o.name, is_personal: o.is_personal, wrapped_org_key: "" }));
      set({ active: true, orgs, snapItems: snap.items, snapFolders: snap.folders, error: null, terminal: false });
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Native errors: "wrong master password" / "...wiped after too many attempts" /
      // "offline cache expired" / "no offline cache" (the last also covers a pre-v2 cache).
      const terminal = /wiped|expired|no offline/i.test(msg);
      set({ error: msg, terminal });
      return false;
    }
  },
  exit: () => set({ active: false, orgs: [], snapItems: [], snapFolders: [], error: null, terminal: false }),
}));

/** Adapted, read-only items for one vault (offline). */
export function offlineItemsFor(orgId: string): DecryptedItem[] {
  return useOffline
    .getState()
    .snapItems.filter((i) => i.org_id === orgId)
    .map(adaptOfflineItem)
    .sort((a, b) => a.content.name.localeCompare(b.content.name));
}

/** Flat folder/device nodes for one vault (offline), ready for AppShell's buildFolderTree. */
export function offlineFoldersFor(orgId: string): OfflineFlatFolder[] {
  return useOffline
    .getState()
    .snapFolders.filter((f) => f.org_id === orgId)
    .map((f) => ({
      id: f.id,
      parentId: f.parent_id ?? null,
      name: f.name,
      kind: f.kind,
      meta: f.kind === "device" && f.meta ? (safeParse(f.meta) as DeviceMeta) : null,
    }));
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
